import { useEffect, useId, useState } from 'react';
import { useRouter } from 'next/router';
import { Plus } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useBasePath } from '../../../lib/use-base-path';
import { IconButton } from '../ui';
import styles from './StickyChatBar.module.css';

export interface StickyCreateItem {
  kind: 'task' | 'routine' | 'goal' | 'contact' | 'app' | 'file';
  labelKey: string;
  /** The first message of the new chat — the same one the home category view's create button sends. */
  seedKey: string;
  biName: string;
  /** The home category tile's own mark, under `public/illustrations/home3/`. */
  art: string;
}

// Goal has no Space here, so it takes the unscoped seed (`newGoalSeedKey(undefined)`).
export const STICKY_CREATE_ITEMS: readonly StickyCreateItem[] = [
  { kind: 'task', labelKey: 'stickyChat.create.task', seedKey: 'tasks.create.prompt', biName: 'create_task', art: 'tasks.svg' },
  { kind: 'routine', labelKey: 'stickyChat.create.routine', seedKey: 'routines.create.prompt', biName: 'create_routine', art: 'routines.svg' },
  { kind: 'goal', labelKey: 'stickyChat.create.goal', seedKey: 'goals.createPrompt', biName: 'create_goal', art: 'goals.svg' },
  { kind: 'contact', labelKey: 'stickyChat.create.contact', seedKey: 'contacts.addContactSeed', biName: 'add_contact', art: 'messages-photo.png' },
  { kind: 'app', labelKey: 'stickyChat.create.app', seedKey: 'apps.create.prompt', biName: 'create_app', art: 'apps.svg' },
  { kind: 'file', labelKey: 'stickyChat.create.file', seedKey: 'categoryView.files.createPrompt', biName: 'new_file', art: 'files.svg' },
];

/**
 * The "+" beside the resting pill on a phone (Figma 21639:656690 / 21791:56015) and the create menu it
 * opens. Each row starts a fresh chat with its category's create message. The button lives in its own
 * fixed slot, outside the pill's, so it can sit above the menu's scrim while the pill stays under it.
 */
export function StickyCreateMenu({ pageName, hidden = false }: { pageName: string; hidden?: boolean }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  const basePath = useBasePath();
  const [open, setOpen] = useState(false);
  const menuId = useId();

  // Every way the menu is dismissed reports the same close, as on the phones.
  const dismiss = () => {
    logPageClickBi({ page_name: pageName, button_name: 'close_create_menu' });
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // The drawer hides the bar; a menu left open under it would come back with it.
  useEffect(() => {
    if (hidden) setOpen(false);
  }, [hidden]);

  // The bar stays mounted across sticky routes, so a back navigation would carry the menu and its scrim along.
  const { asPath } = useRouter();
  useEffect(() => {
    setOpen(false);
  }, [asPath]);

  const toggle = () => {
    if (open) {
      dismiss();
      return;
    }
    logPageClickBi({ page_name: pageName, button_name: 'open_create_menu' });
    setOpen(true);
  };

  const pick = (item: StickyCreateItem) => {
    logPageClickBi({ page_name: pageName, button_name: item.biName });
    setOpen(false);
    openSideChat({ kind: 'new', message: t(item.seedKey) });
  };

  return (
    <>
      {open && <div className={styles.createScrim} aria-hidden data-testid="sticky-create-scrim" onClick={dismiss} />}
      {open && (
        <div id={menuId} role="menu" aria-label={t('stickyChat.create.open')} className={styles.createMenu}>
          {STICKY_CREATE_ITEMS.map((item) => (
            <button key={item.kind} type="button" role="menuitem" className={styles.createRow} onClick={() => pick(item)}>
              <img
                src={`${basePath}/illustrations/home3/${item.art}`}
                alt=""
                width={24}
                height={24}
                className={`${styles.createIcon}${item.kind === 'contact' ? ` ${styles.createIconRound}` : ''}`}
              />
              <span className="sym-type-medium-regular">{t(item.labelKey)}</span>
            </button>
          ))}
        </div>
      )}
      <div className={`${styles.createSlot}${open ? ` ${styles.createSlotOpen}` : ''}${hidden ? ` ${styles.slotHidden}` : ''}`}>
        <IconButton
          variant="contained"
          color="standard"
          size="large"
          aria-label={t(open ? 'stickyChat.create.close' : 'stickyChat.create.open')}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          className={`${styles.createButton}${open ? ` ${styles.createButtonOpen}` : ''}`}
          onClick={toggle}
        >
          <Plus size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
        </IconButton>
      </div>
    </>
  );
}
